import gsap from 'gsap';
import {
  Mesh,
  Object3D,
  PerspectiveCamera,
  Raycaster,
  Vector2,
  Vector3,
} from 'three';
import emitter, {
  EVENT_CLICK_TAG,
  EVENT_NAME_BACK_HOME,
  EVENT_NAME_CHANGE_STATE,
  EVENT_NAME_CHANGE_STATE_FINISH,
  EVENT_NAME_CHANGE_STATE_START,
} from '../utils/mitt';
import { reportEvent } from '../utils/thinking/thinking';
import { STATE_NAME } from '../comon';
import { useGlobalStore } from '../stores/use-global-store';

const CAM_OFFSET = {
  STATE_GLOBAL: 1.5,
  STATE_LOCAL: 0.5,
};
export default class CamController {
  cameraStates: Object3D[];
  isEnabled = false;
  state = 0;
  camera: PerspectiveCamera;
  isAnimating = false;
  camOffsetTarget = new Vector3();
  camOffset = new Vector3();
  camPosition = new Vector3();
  camOffsetScale = 0;
  camTargetOffsetScale = 0;
  lastCamTween?: gsap.core.Tween;
  clickableObjs: Mesh[];
  raycaster = new Raycaster();
  // lastStartViewTime = new Date().getTime();
  constructor(
    camera: PerspectiveCamera,
    cameraStates: Object3D[],
    clickableObjs: Mesh[]
  ) {
    this.camera = camera;
    this.cameraStates = cameraStates;
    this.clickableObjs = clickableObjs;
    emitter.on(EVENT_NAME_BACK_HOME, () => {
      // console.log('EVENT_NAME_BACK_HOME');
      this.stopAnim();
      this.toState(0);
    });
    emitter.on(EVENT_CLICK_TAG, index => {
      // console.log('EVENT_CLICK_TAG');
      this.stopAnim();
      this.toState(index);
    });
    emitter.emit(EVENT_NAME_CHANGE_STATE, this.state);
    // window.addEventListener('click', this.onClick.bind(this));
    window.addEventListener('mousemove', this.onMouseMove.bind(this));

    // 关闭页面前提交报告
    window.addEventListener('beforeunload', event => {
      const current = new Date().getTime();
      reportEvent('brand_page_view', {
        page_state: '退出',
        button_type: STATE_NAME[this.state],
        browsing_time: Math.round((current - this.getLastStartViewTime()) * 0.001),
      });
      this.setLastStartViewTime(current);
    });
    window.addEventListener("unload", () => {
      const globalState = useGlobalStore.getState()
      globalState.setIsBrowser(false)
    })
  }
  setLastStartViewTime(time: number) {
    const {setBrowserTime} = useGlobalStore.getState()
    setBrowserTime(time)
  }
  getLastStartViewTime() {
    const {browserTime} = useGlobalStore.getState()
    return browserTime;
  }
  onClick(e: MouseEvent) {
    const pointer = new Vector2();
    pointer.x = (e.pageX / window.innerWidth) * 2 - 1;
    pointer.y = -(e.pageY / window.innerHeight) * 2 + 1;
    this.raycaster.setFromCamera(pointer, this.camera);

    const intersects = this.raycaster.intersectObjects(this.clickableObjs);
    if (intersects.length > 0) {
      // console.log(intersects, intersects.length, intersects[0]);
      const name = intersects[0].object.name.split('-');
      const index = parseInt(name[1]);
      // console.log(intersects[0].object.name, index);
      this.toState(index);
    }
  }
  stopAnim() {
    this.lastCamTween?.kill();
    this.isAnimating = false;
  }
  toState(index: number) {
    // console.log(
    //   'toState',
    //   index,
    //   this.isEnabled,
    //   this.isAnimating,
    //   this.state,
    //   useGlobalStore.getState().camState
    // );
    if (!this.isEnabled) return;
    if (this.isAnimating) return;
    if (index === this.state) return;
    if (index < 0 || index >= this.cameraStates.length) return;

    const current = new Date().getTime();
    reportEvent('brand_page_view', {
      page_state: '退出',
      button_type: STATE_NAME[this.state],
      browsing_time: Math.round((current - this.getLastStartViewTime()) * 0.001),
    });

    reportEvent('brand_page_view', {
      page_state: '进入',
      button_type: STATE_NAME[index],
      browsing_time: '',
    });
    this.setLastStartViewTime(current);

    this.isAnimating = true;
    const originPos = this.camPosition.clone();
    const originQ = this.camera.quaternion.clone();
    const targetPos = this.cameraStates[index].position.clone();
    const targetQ = this.cameraStates[index].quaternion.clone();
    const animData = { p: 0 };

    let hasEmitFinish = false; // trick
    this.lastCamTween = gsap.to(animData, {
      p: 1,
      duration: 2.8,
      ease: 'power4.inOut',
      onStart: () => {
        emitter.emit(EVENT_NAME_CHANGE_STATE_START, index);
        setTimeout(() => {
          emitter.emit(EVENT_NAME_CHANGE_STATE, index);
          if (index === 0) {
            this.camTargetOffsetScale = CAM_OFFSET.STATE_GLOBAL;
          } else {
            this.camTargetOffsetScale = CAM_OFFSET.STATE_LOCAL;
          }
        }, 1000);

        const setCamState = useGlobalStore.getState().setCamState;
        setCamState(index);
        this.state = index;
      },
      onUpdate: () => {
        this.camPosition.lerpVectors(originPos, targetPos, animData.p);
        this.camera.quaternion.slerpQuaternions(originQ, targetQ, animData.p);

        if (animData.p > 0.5 && !hasEmitFinish) {
          hasEmitFinish = true;
          emitter.emit(EVENT_NAME_CHANGE_STATE_FINISH, index);
        }
      },
      onComplete: () => {
        this.isAnimating = false;
        // this.state = index;
      },
    });
  }
  nextState() {
    const nextIndex = (this.state + 1) % this.cameraStates.length;
    this.toState(nextIndex);
  }
  prevState() {
    const prevIndex =
      (this.state - 1 + this.cameraStates.length) % this.cameraStates.length;
    this.toState(prevIndex);
  }
  onMouseMove(event: MouseEvent) {
    this.camOffsetTarget = new Vector3(
      event.pageX,
      window.innerHeight - event.pageY,
      0
    )
      .sub(new Vector3(window.innerWidth / 2, window.innerHeight / 2, 0))
      .divideScalar(window.innerWidth);
  }
  start() {
    // console.log('CAM CTR START');
    this.camPosition = this.camera.position.clone();
    this.camTargetOffsetScale = CAM_OFFSET.STATE_GLOBAL;
    this.isEnabled = true;


  }
  update() {
    if (!this.isEnabled) return;

    const gloablState = useGlobalStore.getState();
    if (!gloablState.isBrowsering) {
      console.log("isBrowsering is false, set true and init BrowserTime")
      gloablState.setIsBrowser(true)
      gloablState.setBrowserTime(new Date().getTime())
      reportEvent('brand_page_view', {
        page_state: '进入',
        button_type: STATE_NAME[this.state],
        browsing_time: '',
      });
    }

    this.camOffsetScale +=
      (this.camTargetOffsetScale - this.camOffsetScale) * 0.1;
    this.camOffset.lerp(
      this.camOffsetTarget.multiply(new Vector3(1, 0, 0)),
      0.05
    );
    this.camera.position
      .copy(this.camPosition)
      .add(this.camOffset.clone().multiplyScalar(this.camOffsetScale));
  }
}
